/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CommandIntelligenceDrawerContent from './CommandIntelligenceDrawerContent.jsx';
import { getCommandIntelligenceDrawerPaperSx } from './CommandIntelligenceDrawer.styles';
import CommandIntelligenceDrawerResizeHandle from './CommandIntelligenceDrawerResizeHandle.jsx';
import {
  buildCommandIntelligenceAutoHideCacheKey,
  buildCommandIntelligenceDockCacheKey,
  buildCommandIntelligenceLayoutModeCacheKey,
  buildCommandIntelligenceSizeCacheKey,
  clampSizePx,
  DEFAULT_DOCK_ANCHOR,
  DEFAULT_LAYOUT_MODE,
  getDefaultSizePx,
  getPaperPositionSx,
  isValidDockAnchor,
  isValidLayoutMode,
  isSelectableDockAnchor,
  normalizeDockAnchor,
  parsePersistedAutoHide,
} from './drawerLayout';
import { getLegacyDrawerPreferenceKeys, readCachedDrawerPreference } from './drawerPreferences';
import { useCommandIntelligenceContentInsets } from './useCommandIntelligenceContentInsets';
import { useCommandIntelligenceContentPush } from './useCommandIntelligenceContentPush';
import { useCommandIntelligenceOverlapAutoHide } from './useCommandIntelligenceOverlapAutoHide';
import { useTranslateMessage } from '../localization';
import { cacheItem } from '../../util/cache';
import { Drawer, useMediaQuery, useTheme } from '@mui/material';

const readPersistedDockAnchor = widthCacheKey => {
  const legacyKeys = getLegacyDrawerPreferenceKeys(widthCacheKey).map(key => buildCommandIntelligenceDockCacheKey(key));
  const raw = readCachedDrawerPreference(buildCommandIntelligenceDockCacheKey(widthCacheKey), legacyKeys);

  if (isValidDockAnchor(raw)) {
    return normalizeDockAnchor(raw);
  }

  return DEFAULT_DOCK_ANCHOR;
};

const readPersistedSizePx = (widthCacheKey, anchor) => {
  const sizeKey = buildCommandIntelligenceSizeCacheKey(widthCacheKey, anchor);
  const legacyKeys = getLegacyDrawerPreferenceKeys(widthCacheKey).flatMap(key => {
    const keys = [buildCommandIntelligenceSizeCacheKey(key, anchor)];

    if (anchor === DEFAULT_DOCK_ANCHOR) {
      keys.push(key);
    }

    return keys;
  });
  let raw = readCachedDrawerPreference(sizeKey, legacyKeys);

  const parsed = Number(raw);

  if (!Number.isFinite(parsed)) {
    return getDefaultSizePx(anchor);
  }

  return parsed;
};

const readPersistedLayoutMode = widthCacheKey => {
  const legacyKeys = getLegacyDrawerPreferenceKeys(widthCacheKey).map(key =>
    buildCommandIntelligenceLayoutModeCacheKey(key)
  );
  const raw = readCachedDrawerPreference(buildCommandIntelligenceLayoutModeCacheKey(widthCacheKey), legacyKeys);

  if (isValidLayoutMode(raw)) {
    return raw;
  }

  return DEFAULT_LAYOUT_MODE;
};

const readPersistedAutoHide = widthCacheKey => {
  const legacyKeys = getLegacyDrawerPreferenceKeys(widthCacheKey).map(key =>
    buildCommandIntelligenceAutoHideCacheKey(key)
  );
  const raw = readCachedDrawerPreference(buildCommandIntelligenceAutoHideCacheKey(widthCacheKey), legacyKeys);

  return parsePersistedAutoHide(raw);
};

const loadPersistedDrawerState = (widthCacheKey, contentInsets) => {
  const dockAnchor = readPersistedDockAnchor(widthCacheKey);
  const layoutMode = readPersistedLayoutMode(widthCacheKey);
  const autoHideOnClickOutside = readPersistedAutoHide(widthCacheKey);
  const drawerSizePx = clampSizePx(readPersistedSizePx(widthCacheKey, dockAnchor), dockAnchor, contentInsets);

  return {
    dockAnchor,
    layoutMode,
    autoHideOnClickOutside,
    drawerSizePx,
  };
};

const CommandIntelligenceDrawer = ({
  session,
  onClose,
  onAttachItems,
  onDetachItem,
  onClearAttached,
  commandIntelligenceConfig,
  renderContextBar,
  widthCacheKey = 'commandIntelligenceDrawerWidth',
}) => {
  const theme = useTheme();
  const translateMessage = useTranslateMessage();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const contentInsets = useCommandIntelligenceContentInsets();
  const drawerRootRef = useRef(null);

  const [dockAnchor, setDockAnchor] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_DOCK_ANCHOR;
    }

    return loadPersistedDrawerState(widthCacheKey, contentInsets).dockAnchor;
  });

  const [layoutMode, setLayoutMode] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_LAYOUT_MODE;
    }

    return loadPersistedDrawerState(widthCacheKey, contentInsets).layoutMode;
  });

  const [autoHideOnClickOutside, setAutoHideOnClickOutside] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return loadPersistedDrawerState(widthCacheKey, contentInsets).autoHideOnClickOutside;
  });

  const desktopAnchor = dockAnchor;
  const isDrawerOpen = Boolean(session?.open);

  const [drawerSizePx, setDrawerSizePx] = useState(() => {
    if (typeof window === 'undefined') {
      return getDefaultSizePx(DEFAULT_DOCK_ANCHOR);
    }

    return loadPersistedDrawerState(widthCacheKey, contentInsets).drawerSizePx;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const persisted = loadPersistedDrawerState(widthCacheKey, contentInsets);

    setDockAnchor(persisted.dockAnchor);
    setLayoutMode(persisted.layoutMode);
    setAutoHideOnClickOutside(persisted.autoHideOnClickOutside);
    setDrawerSizePx(persisted.drawerSizePx);
  }, [widthCacheKey]);

  useCommandIntelligenceContentPush({
    enabled: !isMobile,
    layoutMode,
    dockAnchor: desktopAnchor,
    drawerSizePx,
    isOpen: isDrawerOpen,
  });

  useCommandIntelligenceOverlapAutoHide({
    enabled: !isMobile,
    isOpen: isDrawerOpen,
    layoutMode,
    autoHideOnClickOutside,
    drawerRootRef,
    onClose,
  });

  const clampSize = useCallback(
    size => {
      return clampSizePx(size, desktopAnchor, contentInsets);
    },
    [contentInsets, desktopAnchor]
  );

  useEffect(() => {
    if (isMobile) {
      return undefined;
    }

    setDrawerSizePx(prev => clampSize(prev));
  }, [clampSize, contentInsets, isMobile]);

  useEffect(() => {
    if (isMobile || !session?.open) {
      return undefined;
    }

    const onKeyDown = event => {
      if (event.key === 'Escape') {
        onClose?.(event, 'escapeKeyDown');
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile, session?.open, onClose]);

  const handleResizeCommit = useCallback(
    size => {
      const clamped = clampSize(size);
      const sizeKey = buildCommandIntelligenceSizeCacheKey(widthCacheKey, desktopAnchor);

      cacheItem(sizeKey, String(clamped));

      if (desktopAnchor === DEFAULT_DOCK_ANCHOR) {
        cacheItem(widthCacheKey, String(clamped));
      }
    },
    [clampSize, desktopAnchor, widthCacheKey]
  );

  const handleDockAnchorChange = useCallback(
    nextAnchor => {
      if (!isSelectableDockAnchor(nextAnchor) || nextAnchor === dockAnchor) {
        return;
      }

      cacheItem(buildCommandIntelligenceDockCacheKey(widthCacheKey), nextAnchor);
      setDockAnchor(nextAnchor);

      const nextSize = readPersistedSizePx(widthCacheKey, nextAnchor);

      setDrawerSizePx(clampSizePx(nextSize, nextAnchor, contentInsets));
    },
    [contentInsets, dockAnchor, widthCacheKey]
  );

  const handleLayoutModeChange = useCallback(
    nextMode => {
      if (!isValidLayoutMode(nextMode) || nextMode === layoutMode) {
        return;
      }

      cacheItem(buildCommandIntelligenceLayoutModeCacheKey(widthCacheKey), nextMode);
      setLayoutMode(nextMode);
    },
    [layoutMode, widthCacheKey]
  );

  const handleAutoHideOnClickOutsideChange = useCallback(
    nextEnabled => {
      const normalized = Boolean(nextEnabled);

      if (normalized === autoHideOnClickOutside) {
        return;
      }

      cacheItem(buildCommandIntelligenceAutoHideCacheKey(widthCacheKey), String(normalized));
      setAutoHideOnClickOutside(normalized);
    },
    [autoHideOnClickOutside, widthCacheKey]
  );

  const resizeAriaLabel = translateMessage('command_intelligence_drawer_resize_handle');
  const activeAnchor = isMobile ? 'bottom' : desktopAnchor;

  const paperSx = useMemo(() => {
    const baseSx = getCommandIntelligenceDrawerPaperSx(theme, { anchor: activeAnchor, isMobile });

    if (isMobile) {
      return baseSx;
    }

    return {
      ...baseSx,
      ...getPaperPositionSx(desktopAnchor, contentInsets, drawerSizePx),
    };
  }, [activeAnchor, contentInsets, desktopAnchor, drawerSizePx, isMobile, theme]);

  return (
    <Drawer
      ref={drawerRootRef}
      anchor={activeAnchor}
      variant={isMobile ? 'temporary' : 'persistent'}
      open={Boolean(session?.open)}
      onClose={onClose}
      PaperProps={{
        sx: paperSx,
      }}
      ModalProps={
        isMobile
          ? {
              keepMounted: false,
            }
          : undefined
      }
    >
      {session?.open ? (
        <CommandIntelligenceDrawerContent
          session={session}
          onClose={onClose}
          onAttachItems={onAttachItems}
          onDetachItem={onDetachItem}
          onClearAttached={onClearAttached}
          commandIntelligenceConfig={commandIntelligenceConfig}
          renderContextBar={renderContextBar}
          dockAnchor={desktopAnchor}
          onDockAnchorChange={handleDockAnchorChange}
          dockPickerEnabled={!isMobile}
          layoutMode={layoutMode}
          onLayoutModeChange={handleLayoutModeChange}
          layoutModePickerEnabled={!isMobile}
          autoHideOnClickOutside={autoHideOnClickOutside}
          onAutoHideOnClickOutsideChange={handleAutoHideOnClickOutsideChange}
        />
      ) : null}
      <CommandIntelligenceDrawerResizeHandle
        enabled={!isMobile && Boolean(session?.open)}
        anchor={desktopAnchor}
        sizePx={drawerSizePx}
        onChangeSize={setDrawerSizePx}
        clampSize={clampSize}
        onCommit={handleResizeCommit}
        ariaLabel={resizeAriaLabel}
      />
    </Drawer>
  );
};

export default CommandIntelligenceDrawer;
